import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { chunk } from 'lodash-es';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import {
  getString,
  stringToDate,
  stringToDateTime,
} from '../../../helpers/stringHelpers.js';
import Grid from '@material-ui/core/Grid';
import LowPriorityIcon from '@material-ui/icons/LowPriority';
import EventOutlineIcon from '@material-ui/icons/EventOutlined';
import PersonIcon from '@material-ui/icons/PersonOutlined';
import TimelineIcon from '@material-ui/icons/Timeline';
import SubjectIcon from '@material-ui/icons/Subject';
import DescriptionIcon from '@material-ui/icons/DescriptionOutlined';
import FlashIcon from '@material-ui/icons/FlashOnOutlined';
import Hidden from '@material-ui/core/Hidden';
import Divider from '@material-ui/core/Divider';
import IndentedCardContent from '../../../components/IndentedCardContent/IndentedCardContent.js';
import BigIconFieldReadOnly from '../../../components/BigIconFieldReadOnly/BigIconFieldReadOnly';
import ReplaceContact from '../../../components/Replace/ReplaceContact/ReplaceContact';

import styles from './styles.js';

class WorkRequestsCard extends React.Component {
  constructor(props) {
    super(props);

    this.makeFields = this.makeFields.bind(this);
    this.makeBody = this.makeBody.bind(this);
    this.layoutClosedOrOpen = this.layoutClosedOrOpen.bind(this);
  }

  isUrgent(priorityDescription) {
    switch (priorityDescription) {
      case 'Urgent health and safety':
      case 'Urgent responsive':
        return true;
      default:
        return false;
    }
  }

  isCancelled(stateDescription) {
    return stateDescription == 'Work cancelled' ? true : false;
  }

  layoutClosedOrOpen(layoutType) {
    return layoutType === 'open' || layoutType === 'closed';
  }

  getAdditionalInformation(
    start,
    end,
    followUpDate,
    priorityDescription,
    noDropIn
  ) {
    const stringsComponent = 'workRequests';

    const timestamp = moment(followUpDate).toISOString();
    const showTime = this.isUrgent(priorityDescription);
    const timestampFormatted = showTime
      ? stringToDateTime(timestamp)
      : stringToDate(timestamp);

    return (
      <React.Fragment>
        {followUpDate && (
          <React.Fragment>
            {start}{' '}
            <span className={this.props.classes.bold}>
              {timestampFormatted}
            </span>
            {end}
          </React.Fragment>
        )}
        {followUpDate && noDropIn && <br />}
        {noDropIn && (
          <React.Fragment>
            <Typography variant="body2">
              {getString(stringsComponent, 'NO_DROP_IN')}
            </Typography>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  makeRow(partA, partB = null, showDivider = true) {
    const ret = [
      <React.Fragment key={partA.label}>
        <Grid item xs={12}>
          {showDivider && <Divider />}
        </Grid>
        <Grid item xs={12} md={partB ? 6 : null} lg={12} xl={partB ? 6 : null}>
          <BigIconFieldReadOnly
            label={partA.label}
            value={partA.value}
            valueSize={'xs'}
            icon={partA.icon}
            classes={partA.classes}
          />
        </Grid>
      </React.Fragment>,
    ];

    if (partB) {
      ret.push(
        <React.Fragment key={partB.label}>
          <Hidden only={['md', 'xl']}>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Hidden>
          <Grid item xs={12} md={6} lg={12} xl={6}>
            <BigIconFieldReadOnly
              label={partB.label}
              value={partB.value}
              valueSize={'xs'}
              icon={partB.icon}
              classes={partB.classes}
            />
          </Grid>
        </React.Fragment>
      );
    }

    return ret;
  }

  makeBody(fields) {
    const { layoutType, classes } = this.props;

    let body = null;

    if (layoutType === 'open') {
      const beforeCollapse = fields.slice(0, 2);
      const afterCollapse = fields.slice(2, 5);
      const fullWidthItems = fields.slice(5);

      body = (
        <React.Fragment>
          <div className={classes.cardContent}>
            <Grid container spacing={16} className={classes.nonCollapsedGrid}>
              {this.makeRow(beforeCollapse[0], beforeCollapse[1], false)}
            </Grid>
            <div>
              <Grid container spacing={16} className={classes.collapsedGrid}>
                {chunk(afterCollapse, 2).map((item, index) =>
                  item.length > 1
                    ? this.makeRow(item[0], item[1])
                    : this.makeRow(item[0])
                )}
                {fullWidthItems.map((item) => this.makeRow(item))}
                <Grid item xs={12} className={classes.detailsDivider}>
                  <Divider />
                </Grid>
              </Grid>
            </div>
          </div>
        </React.Fragment>
      );
    }

    return body;
  }

  makeFields() {
    const {
      classes,
      layoutType,
      data: {
        raisedBy,
        priorityDescription,
        raisedDate,
        stateDescription,
        stateDate,
        stateReason,
        followUpDate,
        comments,
        causedBy,
      },
      contactAction,
    } = this.props;

    const stringsComponent = 'workRequests';

    const fields = [];

    if (this.layoutClosedOrOpen(layoutType)) {
      fields.push({
        label: getString(stringsComponent, 'RAISED_DATE_LABEL'),
        value:
          layoutType == 'open' && this.isUrgent(priorityDescription)
            ? stringToDateTime(raisedDate)
            : stringToDate(raisedDate),
        icon: EventOutlineIcon,
        classes: {
          icon: classes.turquoise,
        },
      });
    }

    if (layoutType === 'open') {
      fields.push({
        label: getString(stringsComponent, 'PRIORITY_LABEL'),
        value: priorityDescription,
        icon: LowPriorityIcon,
        classes: {
          icon: classes.turquoise,
        },
      });
    }

    if (this.layoutClosedOrOpen(layoutType)) {
      fields.push({
        label: getString(stringsComponent, 'STATUS_LABEL'),
        value: stateDescription,
        icon: TimelineIcon,
        classes: {
          icon: classes.turquoise,
        },
      });
    }

    if (this.layoutClosedOrOpen(layoutType)) {
      fields.push({
        label: getString(stringsComponent, 'RAISED_BY_LABEL'),
        value: raisedBy,
        icon: PersonIcon,
        classes: {
          icon: classes.turquoise,
        },
      });
    }

    if (layoutType === 'closed') {
      fields.push({
        label: this.isCancelled(stateDescription)
          ? getString(stringsComponent, 'STATUS_DATE_CANCELLED_LABEL')
          : getString(stringsComponent, 'STATUS_DATE_LABEL'),
        value: stringToDate(stateDate),
        icon: EventOutlineIcon,
        classes: {
          icon: classes.turquoise,
        },
      });
    }

    if (this.layoutClosedOrOpen(layoutType)) {
      fields.push({
        label: getString(stringsComponent, 'STATUS_REASON_LABEL'),
        value: stateReason,
        icon: layoutType == 'open' ? SubjectIcon : DescriptionIcon,
        classes: {
          icon: classes.turquoise,
        },
      });
    }

    if (this.layoutClosedOrOpen(layoutType)) {
      // Split comments string and strip system added comments.
      const commentsFormatted = comments
        ? comments
            .replace(/There is a dog on the property\|?/, '')
            .replace(/ISO\|?/, '')
            .replace(/\|?Willing\||\|?Unwilling\|/, '')
            .replace(/NO DROP IN\|?/, '')
            .split('|')
            .join('\n\n')
        : '';

      fields.push({
        label: 'Job Comments',
        value: commentsFormatted,
        icon: DescriptionIcon,
        classes: {
          icon: classes.turquoise,
        },
      });
    }

    const noDropIn = comments && comments.includes('NO DROP IN|');

    if (layoutType === 'open' && (followUpDate || noDropIn)) {
      fields.push({
        label: getString(stringsComponent, 'ADDITIONAL_INFORMATION_LABEL'),
        value: this.getAdditionalInformation(
          getString(stringsComponent, 'ADDITIONAL_INFORMATION_VALUE_START'),
          <ReplaceContact
            content={getString(
              stringsComponent,
              'ADDITIONAL_INFORMATION_VALUE_END'
            )}
            action={contactAction}
            forceEnglish={true}
            isLink={true}
          />,
          followUpDate,
          priorityDescription,
          noDropIn
        ),
        icon: DescriptionIcon,
        classes: {
          icon: classes.turquoise,
        },
      });
    }

    if (layoutType === 'new') {
      fields.push({
        label: 'Caused By',
        value: causedBy,
        icon: FlashIcon,
        classes: {
          icon: classes.turquoise,
        },
      });
      if (comments) {
        fields.push({
          label: 'Comments',
          value: comments,
          icon: DescriptionIcon,
          classes: {
            icon: classes.turquoise,
          },
        });
      }
    }

    return fields;
  }

  render() {
    const { classes, icon, layoutType, secondaryAction, data, jobNumber } =
      this.props;

    const stringsComponent = 'workRequests';

    return (
      <div className={classes.root}>
        <Card className="Card">
          <IndentedCardContent
            body={this.makeBody(this.makeFields())}
            noIndent={layoutType === 'new' ? true : false}
          />
        </Card>
      </div>
    );
  }
}

WorkRequestsCard.propTypes = {
  classes: PropTypes.object.isRequired,
  icon: PropTypes.object.isRequired,
  layoutType: PropTypes.string,
  data: PropTypes.shape({
    raisedBy: PropTypes.string,
    priorityDescription: PropTypes.string,
    raisedDate: PropTypes.string,
    stateDescription: PropTypes.string,
    stateDate: PropTypes.string,
    stateReason: PropTypes.string,
    comments: PropTypes.string,
    causedBy: PropTypes.string,
  }),
  contactAction: PropTypes.func.isRequired,
  secondaryAction: PropTypes.func,
  jobNumber: PropTypes.string,
};

WorkRequestsCard.defaultProps = {
  layoutType: 'open',
  data: {
    raisedBy: '',
    priorityDescription: '',
    raisedDate: '',
    stateDescription: '',
    stateDate: '',
    stateReason: '',
    comments: null,
    causedBy: '',
  },
  secondaryAction: null,
  jobNumber: null,
};

export default withStyles(styles)(WorkRequestsCard);
